import { format } from "date-fns";
import { Zap } from "lucide-react";
import type { ReactNode } from "react";
import { ClientAppIcon } from "@/components/agents/app-icons";
import { LobuMark } from "@/components/brand/lobu-mark";
import {
	ConnectorMark,
	resolveConnectorDisplay,
} from "@/components/connectors/connector-display";
import { PlatformIcon } from "@/components/devices/device-display";
import type { ConnectorDefinitionItem } from "@/lib/api/connections";
import { formatShortDate } from "@/lib/format-utils";
import type { SourceAttribution } from "@/lib/source-attribution";
import { cn } from "@/lib/utils";

/**
 * The presentational pieces of a stream row, shared by the event card and the
 * notification rows pinned above the stream so both read the same. Kept apart
 * from `event-card.tsx` on purpose: notification rows also render in Lobu
 * Lens, which ships in the entry chunk, and the event card must stay in the
 * lazily loaded feed chunk.
 */

/** Prefer the connector/page mark, then its recorded originator, then the row kind. */
export function EventSourceMark({
	connectorDefinitions,
	sourceUrl,
	label,
	fallback,
	source,
}: {
	connectorDefinitions: ConnectorDefinitionItem[] | null | undefined;
	sourceUrl?: URL | null;
	/** Accessible name when neither a connector nor a page names the source. */
	label: string;
	fallback: ReactNode;
	source?: SourceAttribution | null;
}) {
	const display = source?.platform
		? resolveConnectorDisplay(source.platform, connectorDefinitions ?? [])
		: null;
	const client = source?.client_name || source?.client_id;
	const originator =
		source?.agent_id || source?.agent_name ? (
			<LobuMark className="h-4 w-4 shrink-0" />
		) : client ? (
			<ClientAppIcon name={client} />
		) : source?.device_worker_id || source?.device_platform ? (
			<PlatformIcon platform={source.device_platform ?? null} />
		) : source?.automation_id != null || source?.automation_name ? (
			<Zap className="h-4 w-4" />
		) : (
			fallback
		);
	// A row title or an agent's chosen name is not a connector identity.
	if (!display && !sourceUrl) {
		return (
			<span
				role="img"
				aria-label={label}
				className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground"
			>
				{originator}
			</span>
		);
	}
	return (
		<ConnectorMark
			plain
			name={display?.name ?? sourceUrl?.hostname ?? label}
			icon={display?.icon}
			faviconDomain={display?.faviconDomain ?? sourceUrl?.hostname}
			fallback={originator}
			className="h-5 w-5 rounded-sm text-xs"
		/>
	);
}

/** The muted tile the source mark sits in, left of the row's content; a
 *  notification recolours it. */
export function EventSourceTile({
	children,
	className = "bg-muted text-muted-foreground",
}: {
	children: ReactNode;
	className?: string;
}) {
	return (
		<div
			aria-hidden
			className={cn(
				"mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
				className,
			)}
		>
			{children}
		</div>
	);
}

/** The tile colour that marks a row as needing the user (a notification, or
 *  the page's connection in the side panel). */
export const NOTIFICATION_TILE =
	"bg-blue-500/10 text-blue-600 dark:bg-blue-400/15 dark:text-blue-400";

/** The small line under a row's summary: unread dot, context tag (the
 *  stream's "This page"), then the row kind. */
export function RowKindLine({
	dot,
	tag,
	children,
}: {
	dot?: string;
	tag?: ReactNode;
	children: ReactNode;
}) {
	return (
		<p className="mt-2 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/80">
			{dot ? (
				<span
					aria-hidden
					className={cn("h-1.5 w-1.5 shrink-0 rounded-full", dot)}
				/>
			) : null}
			{tag ? (
				<span className="rounded bg-primary/10 px-1.5 py-0.5 normal-case tracking-normal text-primary">
					{tag}
				</span>
			) : null}
			{children}
		</p>
	);
}

/** The row's clock time beside its title; the full date is in the tooltip. */
export function EventRowTime({ at }: { at: Date }) {
	return (
		<time
			dateTime={at.toISOString()}
			title={formatShortDate(at)}
			className="shrink-0 pt-0.5 text-xs text-muted-foreground"
		>
			{format(at, "HH:mm")}
		</time>
	);
}
